import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSAsyncIntro: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Introduction to Asynchronous JavaScript</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    JavaScript runs on a single thread, yet real apps constantly wait on slow work — network calls, timers, file reads. Async programming is how it stays responsive without freezing.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="16 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Imagine a food delivery app. The moment the user opens it, the frontend needs to fetch several things from servers:</Para>
   <div className="flex flex-wrap gap-2 mb-4">{['User profile', 'Nearby restaurants', 'Offers', 'Past orders', 'Wallet balance'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Para>Each piece of data comes from an API. Assume every call takes <Code>500ms</Code>. A naive implementation looks like this:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = getUser();\nconst restaurants = getRestaurants();\nconst offers = getOffers();\nconst orders = getOrders();` }]} defaultLanguage="javascript" id="async-intro-naive" />
   <Callout tone="info">Should JavaScript stop and wait for each operation to finish before doing anything else?</Callout>
   <Para>If it did, the application would freeze, the user couldn&apos;t interact, and the UI would stop responding. Imagine clicking a button and the whole browser locking up for 5 seconds — nobody would use that app. This is the fundamental problem asynchronous programming solves.</Para>
  </section>

  {/* 2. Why Existing Solutions Fail */}
  <section className="mb-12">
   <SectionHeading n={2}>Why Existing Solutions Fail</SectionHeading>
   <Para>By default, JavaScript runs <Mark>synchronously</Mark> — one statement at a time, each waiting for the previous one:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("A");\nconsole.log("B");\nconsole.log("C");` }]} defaultLanguage="javascript" id="async-intro-sync" />
   <Output>{`A\nB\nC`}</Output>
   <Para>This is fine for fast operations. It falls apart for slow ones, which can take milliseconds, seconds, or even minutes:</Para>
   <div className="flex flex-wrap gap-2">{['Network request', 'Database query', 'File reading', 'Image upload', 'Video processing', 'Payment processing'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Callout tone="warning" title="The cost of waiting">Blocking the single thread for any of these would make the whole application unusable while it waits.</Callout>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Asynchronous programming</Mark> is a technique that lets JavaScript start a slow operation and continue executing other code without waiting for that operation to complete.</p>
   </div>
   <TwoUp
    left={{ title: 'Synchronous', items: ['Start work', 'Wait…', 'Wait…', 'Then continue'] }}
    right={{ title: 'Asynchronous', items: ['Start work', 'Continue working', 'Handle the result later'] }}
   />
  </section>

  {/* 4. Core Idea */}
  <section className="mb-12">
   <SectionHeading n={4}>The Core Idea</SectionHeading>
   <Para>One rule sits at the heart of it: <Mark>don&apos;t block the main thread.</Mark> JavaScript essentially says &quot;start the operation, I&apos;ll keep running, notify me when it&apos;s done.&quot;</Para>
   <CodeBlock snippets={[{ language: 'text', code: `Start API request\n   ↓\nContinue other work\n   ↓\nResponse arrives\n   ↓\nHandle the result` }]} defaultLanguage="text" id="async-intro-coreflow" />
  </section>

  {/* 5. Internal Working */}
  <section className="mb-12">
   <SectionHeading n={5}>Internal Working</SectionHeading>
   <Para>This is one of the most misunderstood interview topics. JavaScript itself has <Mark>one call stack</Mark> — only one piece of code runs at a time:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `task1();\ntask2();\ntask3();\n// strictly: task1 → task2 → task3` }]} defaultLanguage="javascript" id="async-intro-stack" />
   <Para>So how does a single-threaded language handle network calls, timers, file reads, and user events? It doesn&apos;t work alone — it runs inside a <Mark>runtime</Mark> (the browser or Node.js) that provides extra capabilities and does the waiting on its behalf:</Para>
   <TwoUp
    left={{ title: 'Browser provides', items: ['Timers (setTimeout)', 'Network APIs (fetch)', 'DOM APIs'] }}
    right={{ title: 'Node.js provides', items: ['File system APIs', 'Network APIs', 'Thread pool', 'OS integration'] }}
   />
   <Para>When the runtime finishes the slow work, it hands the callback back to JavaScript through the event loop. The engine stays single-threaded; the waiting happens elsewhere.</Para>
  </section>

  {/* 6. Visual Flow */}
  <section className="mb-12">
   <SectionHeading n={6}>Visual Flow</SectionHeading>
   <Para>Walking through a timer step by step shows exactly what happens:</Para>
   <CodeBlock snippets={[{ language: 'text', code: `console.log("Start")      → prints "Start"\n   ↓\nsetTimeout(cb, 2000)      → browser owns the timer\n   ↓\nconsole.log("End")        → prints "End"\n   ↓\n...2 seconds later...\n   ↓\nTimer completes           → cb moved to callback queue\n   ↓\nEvent loop                → call stack empty? push cb\n   ↓\ncb runs                   → prints "Timer Done"` }]} defaultLanguage="text" id="async-intro-visualflow" />
  </section>

  {/* 7. Simple Example */}
  <section className="mb-12">
   <SectionHeading n={7}>Simple Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("Start");\n\nsetTimeout(() => {\n  console.log("Timer Done");\n}, 2000);\n\nconsole.log("End");` }]} defaultLanguage="javascript" id="async-intro-simple" />
   <Para>Most beginners expect <Code>Start → Timer Done → End</Code>. The actual output is:</Para>
   <Output>{`Start\nEnd\nTimer Done`}</Output>
   <Para><Code>setTimeout</Code> doesn&apos;t pause anything — it registers the callback and returns immediately, so <Code>End</Code> prints before the timer fires.</Para>
  </section>

  {/* 8. Deep Example */}
  <section className="mb-12">
   <SectionHeading n={8}>Deep Example: Blocking vs Non-Blocking, Concurrency vs Parallelism</SectionHeading>
   <Para>Two distinctions show up constantly in interviews.</Para>
   <SubHeading>Blocking vs Non-Blocking</SubHeading>
   <TwoUp
    left={{ title: 'Blocking', items: ['Start task → wait → continue', 'Nothing else happens while waiting', 'e.g. a synchronous file read'] }}
    right={{ title: 'Non-Blocking', items: ['Start task → keep working', 'Result is handled later', 'The model modern JS uses'] }}
   />
   <SubHeading>Concurrency vs Parallelism</SubHeading>
   <TwoUp
    left={{ title: 'Concurrency', items: ['Many tasks make progress together', 'Not necessarily at the same instant', 'What JavaScript provides'] }}
    right={{ title: 'Parallelism', items: ['Tasks run literally at the same time', 'Needs multiple CPU cores', 'Web Workers / worker threads'] }}
   />
   <Callout tone="info">JavaScript gives you <Mark>concurrency</Mark> by default, not parallelism. This distinction matters later for the event loop, web workers, and worker threads.</Callout>
  </section>

  {/* 9. Production Example */}
  <section className="mb-12">
   <SectionHeading n={9}>Production Example</SectionHeading>
   <Para>Picture a Node.js server receiving <Mark>10,000 requests</Mark>, each running a database query that takes <Code>500ms</Code>. If Node waited synchronously per request, the server would crawl.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// Non-blocking: start the query, keep serving others\napp.get("/orders", async (req, res) => {\n  const orders = await db.query("SELECT * FROM orders"); // 500ms, non-blocking\n  res.json(orders);\n});` }]} defaultLanguage="javascript" id="async-intro-prod" />
   <Para>Because the wait is offloaded, the single thread keeps handling other requests while each query is in flight — letting Node.js serve thousands of concurrent requests efficiently.</Para>
  </section>

  {/* 10. Engineering Tradeoffs */}
  <section className="mb-12">
   <SectionHeading n={10}>Engineering Tradeoffs</SectionHeading>
   <Para>Async isn&apos;t free. It buys responsiveness and throughput at the cost of more complex control flow:</Para>
   <TwoUp
    left={{ title: 'What you gain', items: ['Responsive UI / non-blocking server', 'High throughput under load', 'Better resource utilization'] }}
    right={{ title: 'What it costs', items: ['Harder-to-follow control flow', 'Error handling across boundaries', 'Race conditions & ordering bugs'] }}
   />
   <Callout tone="warning" title="Async is not magic">It improves responsiveness and throughput — it does not reduce the actual execution time of a single operation.</Callout>
  </section>

  {/* 11. Performance Considerations */}
  <section className="mb-12">
   <SectionHeading n={11}>Performance Considerations</SectionHeading>
   <Para>The single thread is a shared resource. A few rules keep it healthy:</Para>
   <div className="flex flex-wrap gap-2">{['Never block the event loop with CPU-heavy loops', 'Run independent calls in parallel with Promise.all', 'Offload heavy CPU work to Web Workers / worker threads', 'Throughput ≠ latency — one call is still 500ms'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Para>Loading the food-app data <Mark>concurrently</Mark> instead of one-by-one turns four sequential 500ms calls into roughly a single 500ms wait:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const [user, restaurants, offers, orders] = await Promise.all([\n  getUser(),\n  getRestaurants(),\n  getOffers(),\n  getOrders(),\n]);` }]} defaultLanguage="javascript" id="async-intro-perf" />
  </section>

  {/* 12. Common Bugs */}
  <section className="mb-12">
   <SectionHeading n={12}>Common Bugs</SectionHeading>
   <SubHeading>Blocking the event loop</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `while (true) {}\n// freezes the entire application — no events, no rendering` }]} defaultLanguage="javascript" id="async-intro-bug-block" />
   <SubHeading>Reading an async result synchronously</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const result = apiCall(); // async — result isn't ready yet\nconsole.log(result);      // undefined / a pending Promise` }]} defaultLanguage="javascript" id="async-intro-bug-sync" />
   <Para>Async results arrive later — you must wait for them with a callback, <Code>.then()</Code>, or <Code>await</Code>.</Para>
  </section>

  {/* 13. Debugging Stories */}
  <section className="mb-12">
   <SectionHeading n={13}>Debugging Stories</SectionHeading>
   <Callout tone="info" title="The empty-data bug">
    A common first-timer report: &quot;my function returns <Code>undefined</Code>.&quot; The cause is almost always returning a value before an async call resolved — logging inside the callback/<Code>await</Code> shows the data is actually there, just later.
   </Callout>
   <Callout tone="info" title="The frozen page">
    A page that locks up &quot;randomly&quot; often hides a synchronous CPU loop (a giant sort, a huge JSON parse) on the main thread. Moving it to a Web Worker — or chunking it — restores responsiveness.
   </Callout>
  </section >

  {/* 14. Interview Discussion */}
  < section className="mb-12" >
   <SectionHeading n={14}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['What is asynchronous programming?', 'Why does JS need async?', 'Is JavaScript single-threaded?', 'How does JS handle async operations?', 'Blocking vs non-blocking?', 'Concurrency vs parallelism?', "Why doesn't setTimeout block?", 'Role of the browser?', 'Role of Node.js?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="If JavaScript is single-threaded, how does it run async tasks?">
    The engine has one call stack, but it runs inside a runtime (browser/Node). Slow work is handed to runtime APIs (timers, network, file I/O); when done, the callback is queued and the <Code>event loop</Code> pushes it onto the stack once it&apos;s empty.
   </QA>
   <QA q="What's the difference between concurrency and parallelism?">
    Concurrency means multiple tasks make progress together (interleaved on one thread). Parallelism means they execute literally at the same time on multiple cores. JavaScript provides concurrency; true parallelism needs Web Workers or worker threads.
   </QA>
   <QA q="Why doesn't setTimeout block the rest of the code?">
    It only registers a callback with the timer API and returns immediately. The callback runs later via the event loop, so the code after it executes first.
   </QA>
  </section >

  {/* 15. Mental Model */}
  < section className="mb-12" >
   <SectionHeading n={15}>Mental Model</SectionHeading>
   <Para>Think of a restaurant token system: place your order, receive a token, sit with friends, and get notified when the food is ready. You&apos;re free to do other things while the kitchen works.</Para>
   <Para>That token is exactly a <Mark>callback, promise, or future result</Mark> — a handle that lets you be reached once the slow work finishes.</Para>
  </section >

  {/* 16. Interview One-Liner */}
  < section className="mb-12" >
   <SectionHeading n={16}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Asynchronous programming lets JavaScript start long-running operations and continue executing other work without blocking the main thread, improving responsiveness and scalability.
    </p>
   </div>
  </section >

  {/* 17. Most Important Takeaway */}
  < section className="mb-4" >
   <SectionHeading n={17}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    JavaScript is single-threaded, but modern apps constantly perform slow operations — network requests, database queries, file processing, timers. Asynchronous programming lets these run without freezing the application, forming the foundation for callbacks, promises, async/await, and the event loop.
   </Callout>
  </section >
 </div >
);

export default JSAsyncIntro;
